import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import client from "@/lib/api";
import { FeedPathTag } from "@/components/FeedPathTag";
import { Pencil, Save, Plus, Trash2, LayoutGrid } from "lucide-react";
import { VersionTag } from "@/components/VersionTag";

const TEXTAREA_CLASS =
  "flex w-full min-h-[100px] rounded-md border border-input bg-transparent px-3 py-2 text-xs font-mono shadow-sm " +
  "transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 " +
  "focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

const emptyDraft = () => ({ external_id: "", name: "", company: "", status: true, configText: "{}" });

function fromModule(mod) {
  return {
    external_id: mod.external_id,
    name: mod.name,
    company: mod.company,
    status: mod.status,
    configText: JSON.stringify(mod.config || {}, null, 2),
  };
}

function ModuleForm({ draft, setDraft, disabled, showExternalId }) {
  return (
    <div className="grid grid-cols-1 gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {showExternalId && (
          <div>
            <Label className="data-label mb-1 flex items-center gap-1">
              Module ID
              <FeedPathTag path="Dealer.WebGenerator.Module.Modul.id" />
            </Label>
            <Input data-testid="module-form-external-id" disabled={disabled} value={draft.external_id}
              onChange={(e) => setDraft({ ...draft, external_id: e.target.value })} />
          </div>
        )}
        <div>
          <Label className="data-label mb-1 flex items-center gap-1">
            Name
            <FeedPathTag path="Dealer.WebGenerator.Module.Modul.name" />
          </Label>
          <Input data-testid="module-form-name" disabled={disabled} value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <Label className="data-label mb-1 flex items-center gap-1">
            Company
            <FeedPathTag path="Dealer.WebGenerator.Module.Modul.company" />
          </Label>
          <Input data-testid="module-form-company" disabled={disabled} value={draft.company}
            onChange={(e) => setDraft({ ...draft, company: e.target.value })} />
        </div>
        <div className="flex items-end pb-1.5">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox data-testid="module-form-status" checked={draft.status} disabled={disabled}
              onCheckedChange={(v) => setDraft({ ...draft, status: !!v })} />
            Active
            <FeedPathTag path="Dealer.WebGenerator.Module.Modul.status" />
          </label>
        </div>
      </div>
      <div>
        <Label className="data-label mb-1 flex items-center gap-1">
          Config (JSON)
          <FeedPathTag path="Dealer.WebGenerator.Module.Modul.config" />
        </Label>
        <textarea
          data-testid="module-form-config"
          disabled={disabled}
          className={TEXTAREA_CLASS}
          value={draft.configText}
          onChange={(e) => setDraft({ ...draft, configText: e.target.value })}
        />
      </div>
    </div>
  );
}

/** Web generator module list — synced from the feed every import run
 * (upsert by external_id). Unlike Employees, absent modules are NEVER
 * soft-deleted by the importer — module coverage is documented as volatile
 * upstream, so a module missing from one run isn't meaningful. */
export function WebModulesCard({ cardVersion, webModules, dealerId, onWebModulesChanged, onDealerVersionChanged }) {
  const [editingId, setEditingId] = useState(null); // module id, or "new"
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const startEdit = (mod) => {
    setDraft(fromModule(mod));
    setEditingId(mod.id);
  };

  const startAdd = () => {
    setDraft(emptyDraft());
    setEditingId("new");
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const parseConfig = () => {
    try {
      return JSON.parse(draft.configText || "{}");
    } catch {
      toast.error("Config must be valid JSON");
      return undefined;
    }
  };

  const save = async () => {
    const config = parseConfig();
    if (config === undefined) return;
    setSaving(true);
    try {
      if (editingId === "new") {
        if (!draft.external_id.trim() || !draft.name.trim()) {
          toast.error("Module ID and name are required");
          setSaving(false);
          return;
        }
        const { data } = await client.post(`/dealers/${dealerId}/web-modules`, {
          external_id: draft.external_id, name: draft.name, company: draft.company,
          status: draft.status, config,
        });
        onWebModulesChanged((prev) => [...prev, data]);
      } else {
        const { data } = await client.patch(`/web-modules/${editingId}`, {
          name: draft.name, company: draft.company, status: draft.status, config,
        });
        onWebModulesChanged((prev) => prev.map((m) => (m.id === data.id ? data : m)));
      }
      onDealerVersionChanged?.();
      toast.success("Saved");
      cancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (mod) => {
    if (!window.confirm(`Remove module "${mod.name}"?`)) return;
    try {
      await client.delete(`/web-modules/${mod.id}`);
      onWebModulesChanged((prev) => prev.filter((m) => m.id !== mod.id));
      onDealerVersionChanged?.();
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="web-modules-card">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <LayoutGrid className="h-4 w-4 text-muted-foreground" />
          <h2 className="flex items-center gap-1.5 text-lg font-semibold">Web Modules ({webModules.length}) <VersionTag version={cardVersion} testId="version-tag-web_modules" /></h2>
        </div>
        {editingId === null && (
          <Button data-testid="add-module-btn" variant="ghost" size="sm" className="gap-1.5" onClick={startAdd}>
            <Plus className="h-3.5 w-3.5" />
            Add module
          </Button>
        )}
      </div>

      {editingId === "new" && (
        <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 p-3">
          <ModuleForm draft={draft} setDraft={setDraft} disabled={saving} showExternalId />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
            <Button data-testid="save-new-module-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              Save
            </Button>
          </div>
        </div>
      )}

      {webModules.length === 0 && editingId !== "new" && (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="web-modules-empty">
          No web modules on file for this dealer.
        </p>
      )}

      <div className="space-y-3">
        {webModules.map((mod) => (
          <div key={mod.id} className="rounded-md border border-border p-3" data-testid={`module-row-${mod.id}`}>
            {editingId === mod.id ? (
              <>
                <ModuleForm draft={draft} setDraft={setDraft} disabled={saving} showExternalId={false} />
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
                  <Button data-testid={`save-module-${mod.id}`} size="sm" onClick={save} disabled={saving} className="gap-1.5">
                    <Save className="h-3.5 w-3.5" />
                    Save
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium">
                    {mod.name}{" "}
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      #{mod.external_id}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {mod.company || "—"} · {mod.status ? "Active" : "Inactive"}
                  </p>
                </div>
                {editingId === null && (
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-module-${mod.id}`} onClick={() => startEdit(mod)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-module-${mod.id}`} onClick={() => remove(mod)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
